import React, { useState, useEffect } from 'react';
import { MapPin, Clock, Phone, Scissors, Users, Star, Award, Calendar, Quote, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const whatsappNumber = "34603404283";
  const whatsappMessage = encodeURIComponent("Hola 👋 ¿Tenéis sitio ahora para un corte?");

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
  };

  const handleInstagramClick = () => {
    window.open('https://www.instagram.com/peluqueria.de.la.mar/', '_blank');
  };

  const services = [
    {
      icon: Scissors,
      title: "Corte de Pelo",
      titleEn: "Haircut",
      description: "Hombre, mujer, niños. Cortes modernos o clásicos según prefieras.",
      descriptionEn: "Men, women, kids. Modern or classic cuts as you prefer."
    },
    {
      icon: Users,
      title: "Arreglo de Barba",
      titleEn: "Beard Grooming",
      description: "Perfilado, recorte y cuidado de barba profesional.",
      descriptionEn: "Professional beard trimming, shaping and care."
    }
  ];

  const features = [
    {
      icon: Calendar,
      title: "Sin Cita Previa",
      titleEn: "Walk-in Welcome",
      description: "Ven directamente. No necesitas reservar.",
      descriptionEn: "Come directly. No need to book."
    },
    {
      icon: Clock,
      title: "Abiertos Todos los Días",
      titleEn: "Open Every Day",
      description: "Lunes a Domingo de 10:00 a 22:00",
      descriptionEn: "Monday to Sunday 10:00 - 22:00"
    },
    {
      icon: Award,
      title: "Local Moderno y Limpio",
      titleEn: "Modern & Clean",
      description: "Instalaciones nuevas en el centro de S'Illot",
      descriptionEn: "New facilities in the center of S'Illot"
    },
    {
      icon: Star,
      title: "Precios Justos",
      titleEn: "Fair Prices",
      description: "Calidad profesional a precio razonable",
      descriptionEn: "Professional quality at reasonable price"
    }
  ];

  const reviews = [
    {
      name: "Carlos M.",
      initials: "CM",
      location: "S'Illot",
      rating: 5,
      comment: "Excelente servicio y muy profesionales. El local es moderno y limpio. Muy recomendable.",
      commentEn: "Excellent service and very professional. The place is modern and clean. Highly recommended.",
      date: "Hace 2 semanas"
    },
    {
      name: "Thomas K.",
      initials: "TK",
      location: "Tourist from Germany",
      rating: 5,
      comment: "¡Perfecto! Me atendieron sin cita previa y el resultado fue increíble. Very good!",
      commentEn: "Perfect! They attended me without an appointment and the result was incredible. Very good!",
      date: "Hace 1 mes"
    },
    {
      name: "Miguel A.",
      initials: "MA",
      location: "Manacor",
      rating: 5,
      comment: "Llevo viniendo varios meses. Siempre atentos y el corte impecable. Precio muy justo.",
      commentEn: "I've been coming for several months. Always attentive and impeccable cut. Very fair price.",
      date: "Hace 3 semanas"
    },
    {
      name: "Sarah L.",
      initials: "SL",
      location: "Porto Cristo",
      rating: 5,
      comment: "Mi marido quedó encantado con el corte y la barba. Ambiente muy profesional.",
      commentEn: "My husband was delighted with the haircut and beard. Very professional atmosphere.",
      date: "Hace 2 meses"
    }
  ];

  return (
    <div className="min-h-screen bg-dark-surface">
      {/* Floating Social Buttons */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
        {/* Instagram Button */}
        <button
          onClick={handleInstagramClick}
          className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 hover:from-purple-600 hover:via-pink-600 hover:to-orange-500 text-white rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 group"
          aria-label="Síguenos en Instagram"
          title="@peluqueria_de_la_mar_"
        >
          <Instagram className="w-6 h-6 group-hover:rotate-12 transition-transform" />
        </button>

        {/* WhatsApp Button */}
        <button
          onClick={handleWhatsAppClick}
          className="bg-whatsapp hover:bg-whatsapp-hover text-white rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 group"
          aria-label="Consultar disponibilidad por WhatsApp"
          title="¿Vienes hoy? Consúltanos"
        >
          <Phone className="w-6 h-6 group-hover:rotate-12 transition-transform" />
        </button>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_sillot-hairshop/artifacts/v49ytq11_WhatsApp%20Image%202026-01-31%20at%2010.36.13%20%281%29.jpeg)',
            filter: 'brightness(0.4)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-dark-surface/60 via-transparent to-dark-surface" />
        
        <div className={`relative z-10 max-w-6xl mx-auto px-6 py-20 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h1 className="hero-title text-white mb-6">
            Peluquería de la Mar
          </h1>
          <p className="hero-subtitle text-white/90 mb-4">
            Corte y barba bien hechos, sin cita previa
          </p>
          <p className="text-xl md:text-2xl text-white/80 mb-2">
            Estamos en el centro de S'Illot
          </p>
          <p className="text-xl md:text-2xl text-accent-amber mb-8 font-semibold">
            Abierto todos los días · 10:00 - 22:00
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              onClick={handleWhatsAppClick}
              className="cta-button text-lg px-8 py-6"
            >
              <Phone className="mr-2 w-5 h-5" />
              Escribir ahora
            </Button>
            <a href="#ubicacion" className="text-white hover:text-accent-amber transition-colors duration-300 text-lg">
              Ver Ubicación →
            </a>
          </div>
          <p className="text-white/60 text-sm mt-4">
            Te confirmamos disponibilidad al momento
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 px-6 bg-dark-card">
        <div className="max-w-6xl mx-auto">
          <h2 className="section-title text-center mb-4">
            Nuestros Servicios
          </h2>
          <p className="text-center text-text-subtle mb-16 text-lg">
            Our Services
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card 
                key={index} 
                className="service-card group"
              >
                <CardContent className="p-8">
                  <div className="flex items-start gap-6">
                    <div className="service-icon-wrapper">
                      <service.icon className="w-8 h-8" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-accent-amber transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-text-subtle text-sm mb-3">{service.titleEn}</p>
                      <p className="text-white/80 mb-2">{service.description}</p>
                      <p className="text-text-subtle text-sm italic">{service.descriptionEn}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-white/70 mb-4 text-lg">
              <span className="text-accent-amber font-semibold">Atendemos sin cita previa.</span> Escríbenos y te decimos si podemos atenderte ahora.
            </p>
            <Button 
              onClick={handleWhatsAppClick}
              className="cta-button text-lg px-8 py-6"
            >
              <Phone className="mr-2 w-5 h-5" />
              Escribir ahora
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 px-6 bg-dark-surface relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-accent-amber rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent-amber rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <h2 className="section-title text-center mb-4">
            ¿Por Qué Elegirnos?
          </h2>
          <p className="text-center text-text-subtle mb-16 text-lg">
            Why Choose Us?
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="feature-card group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="feature-icon-wrapper">
                  <feature.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-accent-amber transition-colors">
                  {feature.title}
                </h3>
                <p className="text-text-subtle text-sm mb-3 italic">{feature.titleEn}</p>
                <p className="text-white/70 text-sm leading-relaxed">
                  {feature.description}
                </p>
                <p className="text-text-subtle text-xs mt-2 italic">
                  {feature.descriptionEn}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 px-6 bg-dark-card">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="section-title mb-8">
            Confianza y Profesionalismo
          </h2>
          <p className="text-text-subtle mb-6 text-lg italic">Trust & Professionalism</p>
          
          <div 
            className="relative rounded-2xl overflow-hidden shadow-2xl mb-12"
            style={{ height: '400px' }}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_sillot-hairshop/artifacts/j04p8f1a_WhatsApp%20Image%202026-01-31%20at%2010.36.13%20%282%29.jpeg"
              alt="Zona de espera de Peluquería de la Mar"
              className="w-full h-full object-cover"
            />
          </div>

          <p className="text-white/80 text-lg leading-relaxed max-w-2xl mx-auto mb-8">
            Llevamos años cortando pelo y barba en S'Illot. 
            Trabajamos bien, rápido y sin complicaciones. 
            Local moderno, atención profesional y precio justo.
          </p>
          <p className="text-text-subtle italic">
            We've been cutting hair and beards in S'Illot for years. 
            We work well, fast and without complications. 
            Modern salon, professional service and fair price.
          </p>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-24 px-6 bg-dark-surface">
        <div className="max-w-6xl mx-auto">
          <h2 className="section-title text-center mb-4">
            Lo Que Dicen Nuestros Clientes
          </h2>
          <p className="text-center text-text-subtle mb-16 text-lg">
            What Our Clients Say
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {reviews.map((review, index) => (
              <Card 
                key={index}
                className="review-card group"
              >
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-4">
                    <Avatar className="w-12 h-12 bg-accent-amber/20 border-2 border-accent-amber/30">
                      <AvatarFallback className="bg-accent-amber/10 text-accent-amber font-bold">
                        {review.initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white">{review.name}</h3>
                      <p className="text-text-subtle text-sm">{review.location}</p>
                    </div>
                    <div className="flex gap-1">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-accent-amber text-accent-amber" />
                      ))}
                    </div>
                  </div>

                  <Quote className="w-8 h-8 text-accent-amber/30 mb-3" />
                  
                  <p className="text-white/90 mb-3 leading-relaxed">
                    {review.comment}
                  </p>
                  <p className="text-text-subtle text-sm italic mb-4">
                    {review.commentEn}
                  </p>

                  <p className="text-text-subtle text-xs">{review.date}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12 bg-dark-card/50 rounded-2xl p-8 border border-accent-amber/20">
            <p className="text-white text-xl mb-4 font-semibold">
              ¿Vienes hoy?
            </p>
            <p className="text-white/70 mb-6">
              Escríbenos y te confirmamos disponibilidad ahora mismo
            </p>
            <Button 
              onClick={handleWhatsAppClick}
              className="cta-button text-lg px-8 py-6"
            >
              <Phone className="mr-2 w-5 h-5" />
              Abrir WhatsApp
            </Button>
            <p className="text-white/50 text-sm mt-4">
              Respuesta inmediata · Walk-in welcome
            </p>
          </div>
        </div>
      </section>

      {/* Location & Contact Section */}
      <section id="ubicacion" className="py-24 px-6 bg-dark-surface">
        <div className="max-w-6xl mx-auto">
          <h2 className="section-title text-center mb-4">
            Ubicación y Contacto
          </h2>
          <p className="text-center text-text-subtle mb-16 text-lg">
            Location & Contact · Estamos cerca de ti · We're close to you
          </p>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div className="contact-item group">
                <MapPin className="contact-icon" />
                <div>
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-accent-amber transition-colors">
                    Dirección / Address
                  </h3>
                  <p className="text-white/80">Camí de la Mar, 28</p>
                  <p className="text-white/80">07687 S'Illot, Illes Balears</p>
                </div>
              </div>

              <div className="contact-item group">
                <Clock className="contact-icon" />
                <div>
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-accent-amber transition-colors">
                    Horario / Hours
                  </h3>
                  <p className="text-white/80 font-semibold">Lunes a Domingo</p>
                  <p className="text-white/80">10:00 - 22:00</p>
                  <p className="text-accent-amber mt-2 font-semibold">Sin cita previa · Walk-in welcome</p>
                  <p className="text-white/60 text-sm mt-1">Escríbenos antes de venir y te confirmamos</p>
                </div>
              </div>

              <div className="contact-item group">
                <Phone className="contact-icon" />
                <div>
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-accent-amber transition-colors">
                    WhatsApp
                  </h3>
                  <p className="text-white/80 mb-2">+34 603 40 42 83</p>
                  <p className="text-white/60 text-sm mb-4">
                    Escribe y te confirmamos disponibilidad al momento
                  </p>
                  <Button 
                    onClick={handleWhatsAppClick}
                    className="cta-button"
                  >
                    <Phone className="mr-2 w-4 h-4" />
                    Abrir WhatsApp
                  </Button>
                </div>
              </div>
            </div>

            {/* Google Maps Embed */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[500px] border border-white/10">
              <iframe
                title="Peluquería de la Mar Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3079.8901234567!2d3.3276!3d39.5644!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1297b0e0e0e0e0e0%3A0x0!2sCam%C3%AD%20de%20la%20Mar%2C%2028%2C%2007687%20S&#39;Illot%2C%20Illes%20Balears!5e0!3m2!1sen!2ses!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-dark-surface/90 backdrop-blur-md rounded-lg p-4 border border-white/10">
                <div className="flex items-center gap-3">
                  <MapPin className="w-6 h-6 text-accent-amber flex-shrink-0" />
                  <div>
                    <p className="text-white font-semibold">Camí de la Mar, 28</p>
                    <p className="text-white/70 text-sm">07687 S'Illot, Illes Balears</p>
                  </div>
                  <a
                    href="https://www.google.com/maps/place/Peluqueria+de+la+Mar/@39.5592582,3.3699365,15.51z/data=!4m6!3m5!1s0x1296410072a67c07:0x34f884a1d48beb46!8m2!3d39.5641957!4d3.3706893!16s%2Fg%2F11yjlxpkkd?entry=ttu&g_ep=EgoyMDI2MDIxMC4wIKXMDSoASAFQAw%3D%3D"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto"
                  >
                    <Button size="sm" className="cta-button text-sm">
                      Cómo llegar
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-dark-card border-t border-white/5 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h3 className="text-2xl font-bold text-white mb-2">Peluquería de la Mar</h3>
          <p className="text-text-subtle mb-6">Tu peluquería moderna en S'Illot · Your modern barbershop in S'Illot</p>
          <div className="flex justify-center gap-8 text-sm text-white/60">
            <span>© 2025 Peluquería de la Mar</span>
            <span>•</span>
            <span>Camí de la Mar, 28 · S'Illot</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
